package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.ImageCache
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import co.uk.sacursoftware.ssinventory.util.rememberPlatformImagePicker
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

private val dailyAreas = listOf(
    "cleaning" to "Cleaning",
    "frontOfHouse" to "Front of House",
    "issues" to "Issues",
    "miscellaneous" to "Miscellaneous"
)

private fun DailyRecord?.imagesFor(area: String): List<AppImage> = when (area) {
    "cleaning" -> this?.cleaningImages ?: emptyList()
    "frontOfHouse" -> this?.frontOfHouseImages ?: emptyList()
    "issues" -> this?.issuesImages ?: emptyList()
    "miscellaneous" -> this?.miscellaneousImages ?: emptyList()
    else -> emptyList()
}

private fun DailyRecord?.imageUrls(area: String): List<String> = imagesFor(area).map { it.imageUrl }

private val weekDays = listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday")

private fun weekdayLabel(date: LocalDate): String =
    date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DailyScreen(
    onBack: () -> Unit,
    onOpenGallery: (area: String, date: String, title: String) -> Unit
) {
    val userRole = LocalUserRole.current
    val canEdit = userRole == UserRole.ADMIN || userRole == UserRole.EDITOR
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    val today = getCurrentLocalDate()
    val todayStr = "${today.year}-${today.month.number.toString().padStart(2, '0')}-${today.dayOfMonth.toString().padStart(2, '0')}"

    val dates = remember(userRole) {
        if (userRole == UserRole.ADMIN) {
            (-3..3).map { i ->
                today.plus(i, DateTimeUnit.DAY).toString()
            }
        } else {
            listOf(todayStr)
        }
    }

    var selectedDate by remember { mutableStateOf(todayStr) }
    var dailyRecord by remember { mutableStateOf<DailyRecord?>(null) }
    var routines by remember { mutableStateOf<List<DailyRoutine>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var editingRoutine by remember { mutableStateOf<DailyRoutine?>(null) }
    var showAddRoutine by remember { mutableStateOf(false) }
    var routineToDelete by remember { mutableStateOf<DailyRoutine?>(null) }

    fun loadDaily(date: String, showLoader: Boolean = false) {
        scope.launch {
            if (showLoader) isLoading = true
            repository.getDaily(date).onSuccess { record ->
                dailyRecord = record
            }
            repository.getDailyRoutines(date).onSuccess { list ->
                routines = list
            }
            isLoading = false
        }
    }

    LaunchedEffect(selectedDate) {
        loadDaily(selectedDate, showLoader = true)
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Daily Routine") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    if (canEdit) {
                        ToolbarPlusButton(onClick = { showAddRoutine = true })
                    }
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            if (isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    BrandLoadingSpinner()
                }
            } else {
                val collapse = rememberScrollCollapse()
                val scrollState = rememberScrollState()
                val nested = remember(scrollState, collapse) {
                    collapse.connection { scrollState.value == 0 }
                }
                val dateList = dates.map { LocalDate.parse(it) }
                Column(modifier = Modifier.fillMaxSize()) {
                    if (userRole == UserRole.ADMIN && dates.size > 1) {
                        CollapsingDateTabs(
                            dates = dateList,
                            selectedDate = LocalDate.parse(selectedDate),
                            today = today,
                            collapse = collapse.fraction,
                            onDateSelected = { selectedDate = it.toString() },
                            onExpand = { collapse.expand() }
                        )
                    }
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .nestedScroll(nested)
                            .verticalScroll(scrollState)
                            .padding(top = 16.dp)
                    ) {
                        Text(
                            "Routines",
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                        )
                        if (routines.isEmpty()) {
                            Text(
                                if (canEdit) "No extra routines for this day. Tap + to add a weekly or one-off task."
                                else "No extra routines for this day.",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.outline,
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                            )
                        } else {
                            routines.forEach { routine ->
                                DailyRoutineRow(
                                    routine = routine,
                                    canEdit = canEdit,
                                    onToggle = {
                                        val next = if (routine.isTicked == 1) 0 else 1
                                        routines = routines.map { if (it.id == routine.id) it.copy(isTicked = next) else it }
                                        scope.launch {
                                            repository.toggleDailyRoutine(routine.id, selectedDate).onFailure {
                                                loadDaily(selectedDate, showLoader = false)
                                            }
                                        }
                                    },
                                    onEdit = { editingRoutine = routine },
                                    onDelete = { routineToDelete = routine }
                                )
                            }
                        }

                        Text(
                            "Photos",
                            style = MaterialTheme.typography.titleSmall,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(start = 16.dp, end = 16.dp, top = 20.dp, bottom = 4.dp)
                        )
                        dailyAreas.forEach { (area, title) ->
                            val count = dailyRecord.imagesFor(area).size
                            DailyAreaRow(
                                title = title,
                                photoCount = count,
                                onOpen = { onOpenGallery(area, selectedDate, title) }
                            )
                        }
                    }
                }
            }

            if (showAddRoutine || editingRoutine != null) {
                DailyRoutineEditorDialog(
                    initial = editingRoutine,
                    selectedDate = selectedDate,
                    defaultWeekday = weekdayLabel(LocalDate.parse(selectedDate)),
                    onDismiss = {
                        showAddRoutine = false
                        editingRoutine = null
                    },
                    onSave = { request ->
                        showAddRoutine = false
                        editingRoutine = null
                        scope.launch {
                            repository.saveDailyRoutine(request).onSuccess {
                                loadDaily(selectedDate, showLoader = false)
                            }.onFailure {
                                snackbarHostState.showSnackbar("Could not save routine")
                            }
                        }
                    }
                )
            }

            routineToDelete?.let { routine ->
                AlertDialog(
                    onDismissRequest = { routineToDelete = null },
                    title = { Text("Delete routine?") },
                    text = { Text("“${routine.title}” will be removed${if (routine.repeatType == "weekly") " from every ${routine.dayOfWeek}" else ""}.") },
                    confirmButton = {
                        TextButton(onClick = {
                            val toRemove = routine
                            routineToDelete = null
                            routines = routines.filter { it.id != toRemove.id }
                            scope.launch { repository.deleteDailyRoutine(toRemove.id) }
                        }) {
                            Text("Delete", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { routineToDelete = null }) { Text("Cancel") }
                    }
                )
            }
        }
    }
}

@Composable
private fun DailyRoutineRow(
    routine: DailyRoutine,
    canEdit: Boolean,
    onToggle: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    val isDone = routine.isTicked == 1
    val subtitle = if (routine.repeatType == "weekly") {
        "Every ${routine.dayOfWeek ?: "week"}"
    } else {
        "One-off"
    }
    Card(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp),
        shape = MaterialTheme.shapes.medium,
        colors = CardDefaults.cardColors(
            containerColor = if (isDone) successContainerColor() else MaterialTheme.colorScheme.surface
        ),
        border = BorderStroke(1.dp, if (isDone) successContentColor() else MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Checkbox(checked = isDone, onCheckedChange = { onToggle() })
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    routine.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = if (isDone) successOnContainerColor() else MaterialTheme.colorScheme.onSurface,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = if (isDone) successContentColor() else MaterialTheme.colorScheme.onSurfaceVariant
                )
                if (!routine.notes.isNullOrBlank()) {
                    Text(
                        routine.notes,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.outline,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            if (canEdit) {
                TextButton(onClick = onEdit) { Text("Edit") }
                IconButton(onClick = onDelete) {
                    CloseIcon()
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DailyRoutineEditorDialog(
    initial: DailyRoutine?,
    selectedDate: String,
    defaultWeekday: String,
    onDismiss: () -> Unit,
    onSave: (SaveDailyRoutineRequest) -> Unit
) {
    var title by remember { mutableStateOf(initial?.title ?: "") }
    var notes by remember { mutableStateOf(initial?.notes ?: "") }
    var repeatType by remember { mutableStateOf(initial?.repeatType ?: "weekly") }
    var dayOfWeek by remember { mutableStateOf(initial?.dayOfWeek ?: defaultWeekday) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (initial == null) "Add routine" else "Edit routine") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = title,
                    onValueChange = { title = it },
                    label = { Text("Title") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true
                )
                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text("Notes (optional)") },
                    modifier = Modifier.fillMaxWidth()
                )
                Text("Repeat", style = MaterialTheme.typography.labelMedium)
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    FilterChip(
                        selected = repeatType == "weekly",
                        onClick = { repeatType = "weekly" },
                        label = { Text("Weekly") }
                    )
                    FilterChip(
                        selected = repeatType == "once",
                        onClick = { repeatType = "once" },
                        label = { Text("One-off") }
                    )
                }
                if (repeatType == "weekly") {
                    Text("Day of week", style = MaterialTheme.typography.labelMedium)
                    weekDays.chunked(4).forEach { rowDays ->
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            rowDays.forEach { day ->
                                FilterChip(
                                    selected = dayOfWeek == day,
                                    onClick = { dayOfWeek = day },
                                    label = { Text(day.take(3)) }
                                )
                            }
                        }
                    }
                } else {
                    Text(
                        "This task will only appear on $selectedDate.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    if (title.isBlank()) return@TextButton
                    onSave(
                        SaveDailyRoutineRequest(
                            id = initial?.id,
                            title = title.trim(),
                            notes = notes.trim().ifBlank { null },
                            repeatType = repeatType,
                            dayOfWeek = if (repeatType == "weekly") dayOfWeek else null,
                            date = if (repeatType == "once") selectedDate else null
                        )
                    )
                },
                enabled = title.isNotBlank()
            ) { Text("Save", fontWeight = FontWeight.Bold) }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel") }
        }
    )
}

@Composable
private fun DailyAreaRow(
    title: String,
    photoCount: Int,
    onOpen: () -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onOpen),
        color = MaterialTheme.colorScheme.surfaceVariant,
        tonalElevation = 0.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )
            Text(
                text = if (photoCount == 0) "Empty" else if (photoCount == 1) "1 photo" else "$photoCount photos",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1
            )
            Surface(
                shape = MaterialTheme.shapes.small,
                color = MaterialTheme.colorScheme.primary
            ) {
                Text(
                    text = "Open  ›",
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onPrimary,
                    modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp)
                )
            }
        }
    }
    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DailyGalleryScreen(
    area: String,
    date: String,
    title: String,
    onBack: () -> Unit,
    onNavigateToImageViewer: (List<String>, Int, String?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val storageService = remember { FirebaseStorageService() }

    var dailyRecord by remember { mutableStateOf<DailyRecord?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var isUploading by remember { mutableStateOf(false) }
    var showAddSheet by remember { mutableStateOf(false) }
    var imageToDelete by remember { mutableStateOf<AppImage?>(null) }

    val images = dailyRecord.imagesFor(area)

    fun loadDaily(showLoader: Boolean = false) {
        scope.launch {
            if (showLoader) isLoading = true
            repository.getDaily(date).onSuccess { record ->
                dailyRecord = record
            }
            isLoading = false
        }
    }

    val imagePicker = rememberPlatformImagePicker { bytes, name ->
        isUploading = true
        uploadImageToArea(area, bytes, name, date, dailyRecord, repository, storageService, scope) {
            loadDaily(showLoader = false)
            isUploading = false
        }
    }

    LaunchedEffect(date, area) {
        loadDaily(showLoader = true)
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        ScreenTitle(title)
                        Text(
                            DateTimeUtils.formatDisplayDate(LocalDate.parse(date)),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    ToolbarPlusButton(onClick = { if (!isUploading) showAddSheet = true })
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            when {
                isLoading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                }
                images.isEmpty() && !isUploading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            "No photos yet. Tap + to add.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.outline
                        )
                    }
                }
                else -> {
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(minSize = 110.dp),
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(12.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        itemsIndexed(items = images, key = { _, img -> img.id }) { index, img ->
                            Card(
                                modifier = Modifier
                                    .aspectRatio(1f)
                                    .clickable { onNavigateToImageViewer(images.map { it.imageUrl }, index, title) },
                                shape = MaterialTheme.shapes.medium,
                                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.25f))
                            ) {
                                Box(modifier = Modifier.fillMaxSize()) {
                                    RemoteImageView(
                                        url = img.imageUrl,
                                        imageId = img.id,
                                        modifier = Modifier.fillMaxSize().clip(MaterialTheme.shapes.medium),
                                        contentScale = ContentScale.Crop
                                    )
                                    IconButton(
                                        onClick = { imageToDelete = img },
                                        modifier = Modifier.align(Alignment.TopEnd).size(36.dp)
                                    ) {
                                        Box(
                                            modifier = Modifier
                                                .size(24.dp)
                                                .background(Color.Black.copy(alpha = 0.55f), CircleShape),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            CloseIcon(modifier = Modifier.size(14.dp), tint = Color.White)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (isUploading) {
                Surface(
                    modifier = Modifier.align(Alignment.BottomCenter).fillMaxWidth().padding(16.dp),
                    shape = MaterialTheme.shapes.medium,
                    color = MaterialTheme.colorScheme.surface,
                    tonalElevation = 4.dp
                ) {
                    Row(
                        modifier = Modifier.padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        CircularProgressIndicator(modifier = Modifier.size(20.dp))
                        Text("Uploading photo...", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }

    if (showAddSheet) {
        ModalBottomSheet(
            onDismissRequest = { showAddSheet = false },
            containerColor = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurface
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(start = 8.dp, end = 8.dp, bottom = 32.dp)
            ) {
                Text(
                    "Add photo",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                )
                DailyAddSourceRow(
                    label = "Picture",
                    icon = Res.drawable.delivery
                ) {
                    showAddSheet = false
                    imagePicker.takePhoto()
                }
                DailyAddSourceRow(
                    label = "Gallery",
                    icon = Res.drawable.folder
                ) {
                    showAddSheet = false
                    imagePicker.pickFromGallery()
                }
            }
        }
    }

    imageToDelete?.let { image ->
        AlertDialog(
            onDismissRequest = { imageToDelete = null },
            title = { Text("Delete photo?") },
            text = { Text("This photo will be removed from $title.") },
            confirmButton = {
                TextButton(onClick = {
                    val toRemove = image
                    imageToDelete = null
                    scope.launch { ImageCache.evict(toRemove.imageUrl, toRemove.id) }
                    removeImageFromArea(area, toRemove.imageUrl, date, dailyRecord, repository, storageService, scope) {
                        loadDaily(showLoader = false)
                    }
                }) {
                    Text("Delete", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { imageToDelete = null }) { Text("Cancel") }
            }
        )
    }
}

@Composable
private fun DailyAddSourceRow(
    label: String,
    icon: org.jetbrains.compose.resources.DrawableResource,
    onClick: () -> Unit
) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 4.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.surfaceVariant
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Image(
                painter = painterResource(icon),
                contentDescription = label,
                modifier = Modifier.size(24.dp),
                contentScale = ContentScale.Fit
            )
            Text(
                label,
                fontWeight = FontWeight.SemiBold,
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

fun uploadImageToArea(
    area: String,
    bytes: ByteArray,
    fileName: String,
    date: String,
    record: DailyRecord?,
    repository: SSInventoryRepository,
    storageService: FirebaseStorageService,
    scope: kotlinx.coroutines.CoroutineScope,
    onComplete: () -> Unit
) {
    scope.launch {
        try {
            val existingUrls = record.imageUrls(area)
            val newUrl = storageService.uploadImage(bytes, StorageArea.DAILY)
            val request = SaveDailyImagesRequest(
                date = date,
                area = area,
                images = existingUrls + newUrl
            )
            repository.saveDailyImages(request)
        } catch (_: Exception) {}
        onComplete()
    }
}

fun removeImageFromArea(
    area: String,
    urlToRemove: String,
    date: String,
    record: DailyRecord?,
    repository: SSInventoryRepository,
    storageService: FirebaseStorageService,
    scope: kotlinx.coroutines.CoroutineScope,
    onComplete: () -> Unit
) {
    scope.launch {
        try {
            storageService.deleteOrderImage(urlToRemove)
            val request = SaveDailyImagesRequest(
                date = date,
                area = area,
                images = record.imageUrls(area).filter { it != urlToRemove }
            )
            repository.saveDailyImages(request)
        } catch (_: Exception) {}
        onComplete()
    }
}
